import React, { useState, useCallback } from 'react';

import Modal from 'components/partials/modal/modal';
import Button from 'components/partials/button/button';
import Checkbox from 'components/partials/checkbox/checkbox';
import Select from 'components/partials/select/select';
import toast from 'components/partials/toast/toast';

import {
  useCreateRegime,
  useCreateRegimeGroup,
} from '../../../hooks/use-aid-regimes';

import { BASELINE_POPULATIONS } from '../matrix-step.constants';

interface ExistingGroup {
  id: string | number;
  name: string;
}

interface GroupOption {
  value: string | number;
  label: string;
  __isNew__?: boolean;
}

interface AddRegimeModalProps {
  userId: string;
  modelId: string | number;
  existingGroups?: ExistingGroup[];
  onClose: () => void;
  onSuccess?: () => void;
}

const getSelectedPopulationsInCreationOrder = (selectedValues: string[]) =>
  BASELINE_POPULATIONS.filter((population) =>
    selectedValues.includes(population.value)
  ).sort((a, b) => {
    if (a.value === 'COMMUTER') return -1;
    if (b.value === 'COMMUTER') return 1;

    return a.label.localeCompare(b.label);
  });

const AddRegimeModal = ({
  userId,
  modelId,
  existingGroups = [],
  onClose,
  onSuccess,
}: AddRegimeModalProps): React.JSX.Element => {
  const [selectedPopulations, setSelectedPopulations] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GroupOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createRegimeMutation = useCreateRegime();
  const createRegimeGroupMutation = useCreateRegimeGroup();

  const groupOptions = existingGroups.map((group) => ({
    value: group.id,
    label: group.name,
  }));

  const isFormValid = !!selectedPopulations.length && !!selectedGroup;

  const handlePopulationToggle = useCallback(
    (populationValue: string): void => {
      setSelectedPopulations((prev) =>
        prev.includes(populationValue)
          ? prev.filter((p) => p !== populationValue)
          : [...prev, populationValue]
      );
    },
    []
  );

  const handleAdd = useCallback(async () => {
    if (!isFormValid || !selectedGroup) return;

    setIsSubmitting(true);
    let createdNewGroup = false;

    try {
      let groupId: string | number;

      if (selectedGroup.__isNew__) {
        const enabled = !existingGroups.length;
        const newGroup = (await createRegimeGroupMutation.mutateAsync({
          userId,
          modelId,
          data: { name: selectedGroup.label.trim(), enabled },
        })) as { id: string | number };

        groupId = newGroup.id;
        createdNewGroup = true;
      } else {
        groupId = selectedGroup.value;
      }

      const populationsToCreate =
        getSelectedPopulationsInCreationOrder(selectedPopulations);

      for (const population of populationsToCreate) {
        await createRegimeMutation.mutateAsync({
          userId,
          modelId,
          data: {
            name: population.label,
            groupId,
            population: population.value,
          },
        });
      }

      toast.success({
        title:
          populationsToCreate.length === 1
            ? 'Regime created'
            : 'Regimes created',
        message:
          populationsToCreate.length === 1
            ? `"${populationsToCreate[0].label}" has been added successfully.`
            : `${populationsToCreate.length} regimes have been added successfully.`,
      });

      onSuccess?.();
    } catch {
      toast.error({
        title: 'Error',
        message: createdNewGroup
          ? `Group "${selectedGroup.label}" was created, but the regime could not be added. ` +
            'Please try adding the regime again.'
          : 'Failed to create regime. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isFormValid,
    selectedGroup,
    existingGroups.length,
    createRegimeGroupMutation,
    createRegimeMutation,
    userId,
    modelId,
    selectedPopulations,
    onSuccess,
  ]);

  const footer = (
    <div className="flex items-center gap-4">
      <Button
        type="button"
        variant="brand-filled"
        size="md"
        disabled={!isFormValid}
        loading={isSubmitting}
        onClick={handleAdd}
        data-testid="add-regime-button"
      >
        Add
      </Button>
      <Button
        type="button"
        variant="reserve-text"
        size="md"
        onClick={onClose}
        data-testid="cancel-regime-button"
      >
        Cancel
      </Button>
    </div>
  );

  return (
    <Modal
      headerText="Add regime(s)"
      onCloseCallback={onClose}
      showCloseButton
      disableBackdropClick
      footer={footer}
      skipUnmountOnClose
      data-testid="add-regime-modal"
    >
      <div className="mt-4 space-y-3">
        <div>
          <span className="font-semibold text-base">Baseline populations</span>
          <div className="flex gap-5 mt-3">
            {BASELINE_POPULATIONS.map((population) => (
              <Checkbox
                key={population.value}
                labelFor={`population-${population.value}`}
                label={population.label}
                checkboxValue={population.value}
                checked={selectedPopulations.includes(population.value)}
                onChange={() => handlePopulationToggle(population.value)}
              />
            ))}
          </div>
        </div>

        <Select<GroupOption>
          label="Group name"
          labelInTop
          options={groupOptions}
          isCreateble
          placeholder="Select or type a new group name..."
          value={selectedGroup ?? undefined}
          onChange={(option) => {
            if (option && !Array.isArray(option)) {
              setSelectedGroup(option);
            } else {
              setSelectedGroup(null);
            }
          }}
          menuPortalTarget={document.body}
          extraStyles={() => ({
            menuPortal: (base: Record<string, unknown>) => ({
              ...base,
              zIndex: 9999,
            }),
          })}
          data-testid="group-name-select"
        />
      </div>
    </Modal>
  );
};

export default AddRegimeModal;
